import React, { useState, useEffect } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
    Card,
    CardMedia,
    CardContent,
    Typography,
    IconButton,
    Box,
    useMediaQuery,
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

const useStyles = makeStyles((theme) => ({
    root: {
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        padding: theme.spacing(2, 6, 2, 6),
        //background: "var(--lightGrey)",
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2, 4, 2, 4),
        },
    },
    trackContainer: {
        overflow: 'hidden',
        width: '100%',
    },
    track: {
        display: 'flex',
        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        willChange: 'transform',
    },
    cardWrapper: {
        flexShrink: 0,
        padding: theme.spacing(0, 1),
        boxSizing: 'border-box',
    },
    card: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        boxShadow: 'none',
        border: "1px solid var(--darkerGrey)"
    },
    media: {
        height: 300,
    },
    navButton: {
        backgroundColor: "var(--darkBlue)",
        color: "var(--white) !important",
        position: 'absolute',
        borderRadius: 0,
        top: 'calc(50% - 16px)',
        transform: 'translateY(-50%)',
        padding: theme.spacing(2, 1),
        zIndex: 2,
        "& svg": {
            fontSize: "1.1em",
            [theme.breakpoints.down('sm')]: {
                fontSize: "0.8em",
            },
        },
        '&:hover': {
            backgroundColor: "var(--brightBlue)",
        },
        '&.Mui-disabled': {
            backgroundColor: "var(--darkBlue) !important",
            opacity: 0.5,
        },
    },
    prevButton: {
        left: 0,
    },
    nextButton: {
        right: 0,
    },
    indicators: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        bottom: theme.spacing(1),
        zIndex: 2,
        paddingTop: 24
    },
    dot: {
        width: 16,
        height: 16,
        borderRadius: '50%',
        backgroundColor: 'var(--darkBlue)',
        opacity: 0.3,
        margin: theme.spacing(0, 0.5),
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            opacity: 0.7,
        },
    },
    activeDot: {
        opacity: 1,
        width: 32,
        borderRadius: 8,
    },
}));

export default function CardCarousel({ items, autoPlay = false, autoPlayInterval = 4000 }) {
    const classes = useStyles();
    const theme = useTheme();

    const isXs = useMediaQuery(theme.breakpoints.down('xs'));
    const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));

    const getCardsPerView = () => {
        if (isXs) return 1;
        if (isSm) return 3;
        if (isMd) return 4;
        return 5;
    };

    const cardsPerView = getCardsPerView();
    const [currentIndex, setCurrentIndex] = useState(0);
    const maxIndex = Math.max(0, items.length - cardsPerView);
    const totalPages = maxIndex + 1;

    useEffect(() => {
        if (currentIndex > maxIndex) {
            setCurrentIndex(maxIndex);
        }
    }, [cardsPerView, maxIndex, currentIndex]);

    useEffect(() => {
        if (!autoPlay) return;
        const timer = setInterval(() => {
            setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
        }, autoPlayInterval);
        return () => clearInterval(timer);
    }, [autoPlay, autoPlayInterval, maxIndex]);

    const handlePrev = () => {
        setCurrentIndex((prev) => Math.max(prev - 1, 0));
    };

    const handleNext = () => {
        setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
    };

    const cardWidthPercent = 100 / cardsPerView;

    return (
        <Box className={classes.root}>
            <IconButton
                className={`${classes.navButton} ${classes.prevButton}`}
                onClick={handlePrev}
                disabled={currentIndex === 0}
                aria-label="previous slide"
            >
                <ChevronLeftIcon />
            </IconButton>

            <div className={classes.trackContainer}>
                <div
                    className={classes.track}
                    style={{
                        transform: `translateX(-${currentIndex * cardWidthPercent}%)`,
                    }}
                >
                    {items.map((item, index) => (
                        <div
                            key={item.id || index}
                            className={classes.cardWrapper}
                            style={{ width: `${cardWidthPercent}%` }}
                        >
                            <Card className={classes.card} elevation={2}>
                                {item.image && (
                                    <CardMedia
                                        className={classes.media}
                                        image={item.image}
                                        title={item.title}
                                    />
                                )}
                                <CardContent>
                                    <Typography gutterBottom style={{ fontWeight: "bold" }}>
                                        {item.title}
                                    </Typography>
                                    <Typography variant="body2" component="p">
                                        {item.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </div>
                    ))}
                </div>
            </div>

            <IconButton
                className={`${classes.navButton} ${classes.nextButton}`}
                onClick={handleNext}
                disabled={currentIndex >= maxIndex}
                aria-label="next slide"
            >
                <ChevronRightIcon />
            </IconButton>

            {totalPages > 1 && (
                <div className={classes.indicators}>
                    {Array.from({ length: totalPages }).map((_, index) => (
                        <button
                            key={index}
                            className={`${classes.dot} ${currentIndex === index ? classes.activeDot : ''}`}
                            onClick={() => setCurrentIndex(index)}
                            aria-label={`Go to slide group ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </Box>
    );
}